import { createServerFn } from "@tanstack/react-start";
import { generateText } from "ai";
import { z } from "zod";
import { createLovableAiGateway } from "./ai-gateway.server";

const MessageSchema = z.object({
  role: z.enum(["user", "assistant"]),
  content: z.string().min(1).max(4000),
});

const ChatSchema = z.object({
  messages: z.array(MessageSchema).min(1).max(30),
});

const SYSTEM_PROMPT = `You are Katie Fernands' AI assistant on her consulting website. Katie is an inbound lead generation consultant who helps RIAs, banks, and fintech companies grow through content, LinkedIn presence, and referral systems — not cold calls.

About Katie:
- 15+ years experience in financial services marketing
- Has influenced $50M+ AUM for advisor clients
- Generates ~900/month new LinkedIn impressions for clients
- Process: Discovery Call → Strategy Build → Content & Outreach → Leads & Referrals
- Book a call: https://calendly.com/katie-fernands/discovery-call
- Email: katie@katiefernands.com

Your role:
- Be warm, concise, and helpful. 2–4 sentences typically.
- Answer questions about Katie's approach, who she works with, and what to expect.
- For pricing, engagement details, or fit — always encourage booking a Discovery Call or emailing Katie directly.
- Start of every conversation, gently note you're an AI and may be inaccurate; important decisions should go directly to Katie.
- Never invent specific numbers, guarantees, or client names beyond what's above.`;

export const chatWithAssistant = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => ChatSchema.parse(input))
  .handler(async ({ data }) => {
    const key = process.env.LOVABLE_API_KEY;
    if (!key) throw new Error("AI is not configured.");
    const gateway = createLovableAiGateway(key);
    try {
      const { text } = await generateText({
        model: gateway("google/gemini-2.5-flash"),
        system: SYSTEM_PROMPT,
        messages: data.messages,
      });
      return { text };
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      if (message.includes("429")) {
        return { text: "I'm getting a lot of questions right now. Please try again in a moment, or email katie@katiefernands.com." };
      }
      if (message.includes("402")) {
        return { text: "The assistant is temporarily unavailable. Please email katie@katiefernands.com or book a call." };
      }
      console.error("chat error", err);
      return { text: "Sorry, I couldn't answer that. Please reach Katie directly at katie@katiefernands.com." };
    }
  });
